import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';
import { useKnowledge } from '../hooks/useKnowledge';
import { usePageMeta } from '../hooks/usePageMeta';

const PRINCIPLES = [
  { n: '01', icon: 'hub', numClass: 'text-primary-fixed-dim', iconWrap: 'bg-primary-container text-primary', title: 'Everything is connected', desc: 'Poor sleep makes healthy eating harder; regular movement helps mood and sleep. Improving one habit often helps the others.', footIcon: 'sync', foot: 'Connected habits', footWrap: 'bg-surface-container-low text-primary' },
  { n: '02', icon: 'repeat', numClass: 'text-surface-tint', iconWrap: 'bg-surface-tint/10 text-surface-tint', title: 'Small and steady wins', desc: 'Small habits you keep for months do more than big changes you drop after a week.', footIcon: 'trending_up', foot: 'Consistency over intensity', footWrap: 'bg-primary-container text-surface-tint' },
  { n: '03', icon: 'self_improvement', numClass: 'text-on-surface-variant/40', iconWrap: 'bg-surface-container text-outline', title: 'At your own pace', desc: 'Practical changes you choose and adjust yourself, with no pressure and no gadgets.', footIcon: 'check_circle', foot: 'Your choice', footWrap: 'bg-surface-container-low text-outline' },
];

const ENGAGE = [
  { n: 1, dark: true, title: 'Notice where you are', desc: 'Ask yourself which Essential feels hardest right now.', icon: 'psychology', tag: 'Honest start', tagColor: 'text-surface-tint' },
  { n: 2, dark: false, title: 'Pick one Essential', desc: 'Focus on it for 30 days before adding another.', icon: 'filter_center_focus', tag: 'One at a time', tagColor: 'text-primary' },
  { n: 3, dark: true, title: 'Choose 1–2 small habits', desc: 'For example, morning daylight or waking at the same time each day, until they feel automatic.', icon: 'auto_awesome', tag: 'Easy daily habits', tagColor: 'text-surface-tint' },
];

const PORTRAIT = 'https://lh3.googleusercontent.com/aida-public/AB6AXuBnV_Z-10BesL4rIVtSezdquRtXFvlBZgppB-5jF1AqY9ZTMLJWDwkKTafVOqPl2UZAFmSR-fQxhuAQyiuua1A1GRbk5MnmLRjoEiX8DB0-rt0J-_kXIb5H5GbUDW6ZQc6Hl2DyawUxgbhT69XY5A8AJfJabd5p2hlCf2AyuxDRkESixC-cAwWYrKKh7deU0eb2y9S2pk4xTtZ9pyh57oif738Puc44bAoONxGCvdiL9kZUjembmjU';
const SLEEP_IMG = 'https://lh3.googleusercontent.com/aida-public/AB6AXuC6VhNEKRYuRA5g1dbVFm-vq-LzvDPG8baDNFxHOYMDBoBc0ZMCJRMFgVE8uG9Y2r8Z6XAWKic-oE7rz9gry3zBNyfhujoNJ1AO65G9nhz2XdfcyBGLHlm8FaEDMN7Zxur-3MJ5SRnzFi3CD15TQrqqns6uZaaZnG4hCtOFtV_xc67yPXwrE64qBGEiDEcCBrLyAxQmnq28kQmWDqetRlO3qIrzMUgsgv0MyDexLP3uc-g4bDt4tvY';

const plural = (n) => (n === 1 ? '' : 's');

function FoundationCard({ f, highlighted }) {
  const chipBg = highlighted ? 'bg-surface' : 'bg-surface-container';
  return (
    <div data-testid={`foundation-card-${f.code}`} className={`p-space-xl rounded-lg flex flex-col justify-between shadow-sm hover:shadow-md transition-all hover:scale-[1.01] group ${highlighted ? 'bg-primary-container' : 'bg-surface-container-low'}`}>
      <div>
        <div className="flex items-center justify-between mb-space-md">
          <span className={`font-headline-sm text-headline-sm font-bold transition-colors ${highlighted ? 'text-surface-tint group-hover:text-primary' : 'text-outline group-hover:text-surface-tint'}`}>{f.n}</span>
          <span className={`font-label-sm text-label-sm uppercase tracking-widest ${highlighted ? 'text-primary font-bold' : 'text-on-surface-variant'}`}>{f.count} Guide{plural(f.count)}</span>
        </div>
        <h4 className="font-headline-sm text-headline-sm uppercase text-outline mb-space-sm">{f.title}</h4>
        <p className="font-body-md text-body-md text-on-surface-variant mb-space-md leading-relaxed">{f.card}</p>
        <div className="flex flex-wrap gap-1 mb-space-md">
          {f.names.slice(0, 3).map((t) => (
            <Link key={t.slug} to={`/topics/${t.slug}`} className={`px-2 py-0.5 rounded text-outline font-label-sm text-label-sm hover:text-surface-tint transition-colors ${chipBg}`}>{t.title}</Link>
          ))}
        </div>
      </div>
      <Link to={`/essentials/${f.slug}`} className={`inline-flex items-center justify-between pt-space-sm font-label-md text-label-md uppercase font-bold transition-colors ${highlighted ? 'text-surface-tint group-hover:text-primary' : 'text-outline group-hover:text-surface-tint'}`}>
        <span>Start this Essential</span>
        <Icon name="chevron_right" className="text-[16px]" />
      </Link>
    </div>
  );
}

export default function Longevity() {
  usePageMeta({
    title: 'Longevity: Start with the Essentials | OVERALL WELLBEING',
    description:
      'The building blocks of energy, focus and healthy ageing. Explore the Essentials — sleep, movement, nutrition, mental balance and more — and pick the one to start with.',
    path: '/longevity',
    image: '/og-longevity.jpg',
  });

  const { foundations, topics, loading } = useKnowledge();

  const topicNameById = useMemo(() => {
    const m = {};
    topics.forEach((t) => { m[t.canonical_id] = { title: t.title, slug: t.slug }; });
    return m;
  }, [topics]);

  const cards = useMemo(() => foundations.map((f) => ({
    code: f.code,
    slug: f.slug,
    n: String(f.order).padStart(2, '0'),
    title: f.name,
    card: f.card,
    count: (f.mapped_slugs || f.mapped_topic_ids || []).length,
    names: (f.mapped_topic_ids || []).map((id) => topicNameById[id]).filter(Boolean),
  })), [foundations, topicNameById]);

  const spotlight = cards[0];
  const gridNormal = cards.slice(1, 9);
  const highlighted = cards[9];

  return (
    <div className="flex flex-col w-full">
      {/* HERO */}
      <section className="w-full px-margin-mobile lg:px-margin pt-space-xl lg:pt-space-2xl pb-space-2xl bg-surface relative overflow-hidden">
        <div className="absolute top-0 right-1/4 w-[540px] h-[540px] rounded-full bg-primary-container/60 blur-[130px] pointer-events-none -z-10" />
        <div className="absolute -bottom-24 left-10 w-[360px] h-[360px] rounded-full bg-surface-tint/5 blur-[100px] pointer-events-none -z-10" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter items-start">
          <div className="lg:col-span-8 flex flex-col">
            <div className="flex flex-wrap items-center gap-space-sm mb-space-md">
              <span className="w-2 h-2 rounded-full bg-surface-tint shadow-[0_0_8px_rgba(240,40,223,0.7)]" />
              <p className="font-label-md text-label-md uppercase tracking-wider text-on-surface-variant">Where to begin</p>
              <span className="text-surface-container-highest">/</span>
              <span className="font-label-md text-label-md uppercase text-primary tracking-widest">THE ESSENTIALS</span>
            </div>
            <h1 className="font-headline-lg text-headline-lg lg:text-display-hero lg:font-display-hero uppercase text-outline tracking-tight lg:tracking-tighter mb-space-lg leading-tight lg:leading-none">
              THE ESSENTIALS <br />
              <span className="text-surface-tint inline-block drop-shadow-[0_2px_24px_rgba(240,40,223,0.35)]"></span>
            </h1>
            <p className="font-body-lg text-body-lg text-on-surface-variant max-w-3xl mb-space-xl leading-relaxed">
              The everyday habits that matter most for energy, focus and healthy ageing, based on trusted health sources.
            </p>
            <div className="w-full p-space-md bg-surface-container rounded-lg flex flex-wrap items-center gap-space-md">
              <div className="flex items-center gap-space-sm"><span className="w-1.5 h-1.5 rounded-full bg-surface-tint" /><span className="font-label-md text-label-md uppercase tracking-wider text-outline">The Essentials</span></div>
              <span className="hidden sm:inline text-surface-container-highest">•</span>
              <div className="flex items-center gap-space-sm"><span className="w-1.5 h-1.5 rounded-full bg-outline" /><span className="font-label-md text-label-md uppercase tracking-wider text-outline">Practical Guides</span></div>
              <span className="hidden sm:inline text-surface-container-highest">•</span>
              <div className="flex items-center gap-space-sm"><Icon name="verified" className="text-[16px] text-surface-tint" /><span className="font-label-md text-label-md uppercase tracking-wider text-outline">Based on official health guidance</span></div>
              <span className="hidden sm:inline text-surface-container-highest">•</span>
              <div className="flex items-center gap-space-sm"><Icon name="block" className="text-[16px] text-on-surface-variant" /><span className="font-label-md text-label-md uppercase tracking-wider text-on-surface-variant">No gadgets needed</span></div>
            </div>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-space-lg mt-space-lg lg:mt-0">
            <div className="p-space-xl rounded-lg bg-primary-container relative overflow-hidden shadow-sm">
              <div className="absolute -right-6 -bottom-6 text-surface-tint/10 select-none pointer-events-none"><Icon name="architecture" className="text-[140px]" /></div>
              <div className="flex items-center justify-between mb-space-md">
                <span className="font-label-sm text-label-sm uppercase tracking-widest text-primary font-bold">OUR APPROACH</span>
                <span className="font-label-sm text-label-sm uppercase text-on-surface-variant">2026</span>
              </div>
              <blockquote className="font-title-md text-title-md text-outline italic leading-snug mb-space-lg relative z-10">
                “Longevity isn’t about living forever in anxiety. It’s about cultivating everyday physical vigour, mental clarity, and deep peace for decades to come.”
              </blockquote>
              <div className="flex items-center gap-space-md pt-space-sm">
                <div className="w-9 h-9 rounded-full bg-outline flex items-center justify-center text-surface"><Icon name="neurology" className="text-[18px]" /></div>
                <div>
                  <p className="font-label-md text-label-md uppercase text-outline font-bold">Overall Wellbeing Editorial Team</p>
                  <p className="font-body-sm text-body-sm text-on-surface-variant">Clear, practical guidance</p>
                </div>
              </div>
            </div>
            <div className="w-full h-44 rounded-lg overflow-hidden relative shadow-sm">
              <img className="w-full h-full object-cover" alt="Minimalist architectural sculpture in soft museum daylight" src={PORTRAIT} />
              <div className="absolute inset-0 bg-gradient-to-t from-outline/80 via-transparent to-transparent flex items-end p-space-md" />
            </div>
          </div>
        </div>
      </section>

      {/* WHY START */}
      <section className="w-full px-margin-mobile lg:px-margin py-space-2xl bg-surface-container-low">
        <div className="max-w-7xl mx-auto flex flex-col">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-space-md mb-space-2xl">
            <div>
              <div className="flex items-center gap-space-xs mb-space-xs">
                <span className="font-label-md text-label-md text-surface-tint uppercase font-bold tracking-widest">Why start here</span>
              </div>
              <h2 className="font-headline-lg text-headline-lg uppercase text-outline tracking-tight">WHY START WITH THE ESSENTIALS?</h2>
            </div>
            <p className="font-body-md text-body-md text-on-surface-variant max-w-md">Before trying complicated plans or tests, get the basics right. They make the biggest difference.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter">
            {PRINCIPLES.map((p) => (
              <div key={p.n} className="p-space-xl bg-surface rounded-lg flex flex-col justify-between shadow-sm hover:-translate-y-0.5 transition-transform">
                <div>
                  <div className="flex items-center justify-between mb-space-lg">
                    <span className={`font-headline-lg text-headline-lg font-bold ${p.numClass}`}>{p.n}</span>
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center ${p.iconWrap}`}><Icon name={p.icon} className="text-[18px]" /></span>
                  </div>
                  <h3 className="font-headline-sm text-headline-sm text-outline uppercase tracking-tight mb-space-sm">{p.title}</h3>
                  <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed">{p.desc}</p>
                </div>
                <div className={`mt-space-lg rounded p-space-sm flex items-center gap-space-xs ${p.footWrap}`}>
                  <Icon name={p.footIcon} className="text-[16px]" />
                  <span className="font-label-sm text-label-sm uppercase tracking-wider font-bold">{p.foot}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE 10 FOUNDATIONS */}
      <section className="w-full px-margin-mobile lg:px-margin py-space-3xl bg-surface">
        <div className="max-w-7xl mx-auto flex flex-col">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-space-lg mb-space-2xl pb-space-lg">
            <div>
              <div className="flex items-center gap-space-xs mb-space-xs">
                <span className="w-2.5 h-2.5 rounded-full bg-surface-tint" />
                <span className="font-label-md text-label-md uppercase tracking-wider text-surface-tint">THE FULL LIST</span>
              </div>
              <h2 className="font-headline-lg text-headline-lg lg:text-display-hero lg:font-display-hero uppercase text-outline tracking-tight leading-tight">START WITH THE ESSENTIALS</h2>
            </div>
            <div className="flex items-center gap-space-sm self-start lg:self-end">
              <span className="font-body-sm text-body-sm text-on-surface-variant">Each one links to practical guides</span>
              <Icon name="south" className="text-surface-tint text-[18px]" />
            </div>
          </div>

          {loading && (
            <div data-testid="foundations-loading" className="py-space-2xl flex items-center gap-space-sm text-on-surface-variant">
              <span className="inline-block w-2 h-2 rounded-full bg-surface-tint animate-pulse" />
              <span className="font-label-md text-label-md uppercase tracking-wider">Loading the Essentials…</span>
            </div>
          )}

          {/* Spotlight Foundation 01 */}
          {spotlight && (
            <div data-testid="foundation-spotlight" className="w-full rounded-lg bg-surface-container-lowest shadow-md overflow-hidden mb-space-xl grid grid-cols-1 lg:grid-cols-12">
              <div className="lg:col-span-5 relative min-h-[320px] lg:min-h-full">
                <img className="w-full h-full object-cover" alt="Architectural sanctuary in dawn light" src={SLEEP_IMG} />
                <div className="absolute top-space-md left-space-md bg-outline/90 backdrop-blur-md px-space-md py-space-xs rounded text-surface">
                  <span className="font-label-sm text-label-sm uppercase tracking-widest text-primary-fixed-dim">START HERE</span>
                </div>
                <div className="absolute bottom-space-md left-space-md right-space-md bg-surface/90 backdrop-blur-md p-space-md rounded shadow-sm">
                  <p className="font-label-sm text-label-sm text-surface-tint uppercase font-bold tracking-wider">ESSENTIAL {spotlight.n}</p>
                  <p className="font-headline-sm text-headline-sm uppercase text-outline">{spotlight.title}</p>
                </div>
              </div>
              <div className="lg:col-span-7 p-space-xl lg:p-space-2xl flex flex-col justify-between bg-surface-container-lowest">
                <div>
                  <div className="flex items-center justify-between mb-space-md">
                    <span className="px-space-md py-1 rounded-full bg-primary-container text-surface-tint font-label-md text-label-md uppercase font-bold">{spotlight.count} Guide{plural(spotlight.count)}</span>
                    <span className="font-label-md text-label-md text-on-surface-variant uppercase">ESSENTIAL 01</span>
                  </div>
                  <h3 className="font-headline-md text-headline-md uppercase text-outline tracking-tight mb-space-md">{spotlight.title}</h3>
                  <p className="font-body-lg text-body-lg text-on-surface-variant mb-space-lg leading-relaxed">
                    One of the Essentials in OVERALL WELLBEING. The Essentials are the building blocks of energy, focus and healthy ageing — they point you to the guides that matter most, so you always know where to start.
                  </p>
                  {spotlight.names.length > 0 && (
                    <div className="p-space-md bg-primary-container rounded-lg mb-space-lg">
                      <div className="flex items-start gap-space-sm">
                        <Icon name="hub" className="text-surface-tint text-[20px] mt-0.5" />
                        <div>
                          <h4 className="font-label-md text-label-md uppercase text-outline font-bold">Guides In This Path</h4>
                          <p className="font-body-sm text-body-sm text-on-surface-variant mt-0.5">{spotlight.names.map((t) => t.title).join(' • ')}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="mb-space-lg">
                    <p className="font-label-sm text-label-sm uppercase text-on-surface-variant font-bold tracking-wider mb-space-sm">Featured guides in this Essential:</p>
                    <div className="flex flex-wrap gap-space-xs">
                      {spotlight.names.map((t) => (
                        <Link key={t.slug} to={`/topics/${t.slug}`} className="px-space-md py-1.5 rounded-full bg-surface-container text-outline font-body-sm text-body-sm hover:bg-primary-container hover:text-surface-tint transition-colors">{t.title}</Link>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-space-md">
                  <Link to={`/essentials/${spotlight.slug}`} className="inline-flex items-center gap-space-xs text-surface-tint font-label-md text-label-md uppercase tracking-wider font-bold hover:text-primary transition-colors">
                    <span>START THIS ESSENTIAL</span>
                    <Icon name="arrow_forward" className="text-[18px]" />
                  </Link>
                  <div className="flex items-center gap-space-xs text-on-surface-variant font-label-sm text-label-sm uppercase">
                    <Icon name="menu_book" className="text-[16px] text-surface-tint" /><span>{spotlight.count} Guide{plural(spotlight.count)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Grid 02-10 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-gutter">
            {gridNormal.map((f) => (<FoundationCard key={f.code} f={f} />))}
            {highlighted && <FoundationCard f={highlighted} highlighted />}
          </div>
        </div>
      </section>

      {/* HOW TO ENGAGE */}
      <section className="w-full px-margin-mobile lg:px-margin py-space-3xl bg-surface-container">
        <div className="max-w-6xl mx-auto flex flex-col items-center text-center">
          <div className="flex items-center gap-space-xs mb-space-xs">
            <span className="font-label-md text-label-md text-surface-tint uppercase font-bold tracking-widest">METHODOLOGY</span>
            <span className="text-surface-container-highest">/</span>
            <span className="font-label-md text-label-md text-on-surface-variant uppercase">STEP-BY-STEP ADOPTION</span>
          </div>
          <h2 className="font-headline-lg text-headline-lg uppercase text-outline tracking-tight mb-space-md">HOW TO ENGAGE WITH THE ESSENTIALS</h2>
          <p className="font-body-lg text-body-lg text-on-surface-variant max-w-2xl mb-space-2xl">Don’t try to change everything at once.</p>
          <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-gutter text-left">
            {ENGAGE.map((s) => (
              <div key={s.n} className={`p-space-xl rounded-lg relative shadow-sm ${s.dark ? 'bg-surface' : 'bg-primary-container'}`}>
                <div className={`w-10 h-10 rounded-full font-title-md flex items-center justify-center font-bold mb-space-md ${s.dark ? 'bg-outline text-surface' : 'bg-surface-tint text-surface'}`}>{s.n}</div>
                <h3 className="font-headline-sm text-headline-sm uppercase text-outline mb-space-xs">{s.title}</h3>
                <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed mb-space-md">{s.desc}</p>
                <span className={`font-label-sm text-label-sm uppercase font-bold flex items-center gap-1 ${s.tagColor}`}>
                  <Icon name={s.icon} className="text-[14px]" />{s.tag}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GUIDED NEXT STEP */}
      <section className="w-full px-margin-mobile lg:px-margin py-space-3xl bg-surface relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-container/40 to-transparent pointer-events-none" />
        <div className="max-w-4xl mx-auto flex flex-col items-center text-center relative z-10">
          <div className="inline-flex items-center gap-space-xs px-space-md py-1 rounded-full bg-surface-container mb-space-lg">
            <span className="w-2 h-2 rounded-full bg-surface-tint animate-pulse" />
            <span className="font-label-md text-label-md uppercase tracking-wider text-outline font-bold">READY FOR MORE?</span>
          </div>
          <h2 className="font-headline-lg text-headline-lg lg:text-display-hero lg:font-display-hero uppercase text-outline tracking-tight leading-tight lg:leading-none mb-space-md">
            READY TO EXPLORE THE <br />
            <span className="text-surface-tint inline-block drop-shadow-[0_4px_32px_rgba(240,40,223,0.3)]">AREAS OF LIFE?</span>
          </h2>
          <p className="font-body-lg text-body-lg text-on-surface-variant max-w-2xl mb-space-2xl leading-relaxed">
            The Essentials anchor your everyday vitality. Once your baseline feels steady, explore the wider Areas of Life to go deeper on the topics that matter to you.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-space-md w-full justify-center">
            <Link to="/wellbeing" className="w-full sm:w-auto h-12 px-space-xl rounded-lg bg-surface-tint text-on-primary font-label-md text-label-md uppercase tracking-wider flex items-center justify-center gap-space-xs shadow-[0_8px_24px_rgba(240,40,223,0.35)] hover:bg-primary transition-all">
              <span>EXPLORE AREAS OF LIFE</span>
              <Icon name="grid_view" className="text-[18px]" />
            </Link>
            <Link to="/my-login" className="w-full sm:w-auto h-12 px-space-xl rounded-lg bg-outline text-surface font-label-md text-label-md uppercase tracking-wider flex items-center justify-center gap-space-xs hover:bg-on-surface-variant transition-colors">
              <span>Build Everyday Habits</span>
              <Icon name="assignment_turned_in" className="text-[18px]" />
            </Link>
          </div>
          <div className="mt-space-2xl pt-space-lg flex flex-wrap items-center justify-center gap-space-lg text-on-surface-variant">
            {[['verified_user', 'Simple, practical guidance'], ['lock', 'No sponsored content'], ['favorite', 'At your own pace']].map(([ic, t]) => (
              <div key={t} className="flex items-center gap-space-xs"><Icon name={ic} className="text-[18px] text-surface-tint" /><span className="font-body-sm text-body-sm">{t}</span></div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
